import { useEffect, useMemo, useState, useRef } from "react";
import ProductCard from "./ProductCard";
import LearnMore from "./LearnMore";
import { motion, AnimatePresence } from "framer-motion";
void motion;
import { useNavigate, useSearchParams } from "react-router-dom";
import useProductCategories from "./useProductCategories";

const CategoryNav = ({ categories, activeIndex, onSelect, query, onQueryChange }) => {
  const scrollRef = useRef(null);

  useEffect(() => {
    const el = scrollRef.current?.children[activeIndex];
    el?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
  }, [activeIndex]);

  if (categories.length === 0) return null;

  return (
    <div
      id="product-category-nav"
      className="sticky top-14 z-40 border-b border-slate-200 bg-white/95 backdrop-blur-xl md:top-16"
    >
      <div className="relative mx-auto flex max-w-6xl items-center gap-3 px-2 md:px-4">
        {/* Edge fade hints that the row scrolls horizontally on narrow screens */}
        <div className="pointer-events-none absolute left-0 top-0 bottom-0 z-10 w-8 bg-linear-to-r from-white to-transparent md:hidden" />

        <div
          aria-label="Product categories"
          className="flex min-w-0 flex-1 items-center gap-0.5 overflow-x-auto"
          style={{ scrollbarWidth: "none" }}
          ref={scrollRef}
        >
          {categories.map((cat, i) => (
            <button
              type="button"
              key={cat._id}
              onClick={() => onSelect(i)}
              aria-pressed={activeIndex === i}
              className={`relative shrink-0 whitespace-nowrap px-4 py-3.5 text-xs font-semibold tracking-wide transition-colors duration-200 ${
                activeIndex === i
                  ? "text-blue-600"
                  : "text-slate-500 hover:text-slate-800"
              }`}
            >
              <span
                className={`mr-1.5 inline-block h-1.5 w-1.5 rounded-full align-middle ${cat.color}`}
              />
              {cat.title}
              <span className="ml-1.5 text-[10px] opacity-60">
                ({cat.products.length})
              </span>

              {activeIndex === i && (
                <motion.span
                  layoutId="category-tab-underline"
                  className="absolute bottom-0 left-3 right-3 h-0.5 rounded-full bg-blue-600"
                  transition={{ type: "spring", stiffness: 500, damping: 35 }}
                />
              )}
            </button>
          ))}
        </div>

        {/* Compact persistent search, desktop only, keeps search reachable
            once the hero (and its larger search bar) has scrolled away. */}
        <div className="relative hidden shrink-0 py-2 md:block">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 10a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="search"
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder="Search products..."
            aria-label="Search products"
            className="w-44 rounded-lg border border-slate-200 bg-slate-50 py-1.5 pl-8 pr-3 text-xs text-slate-800 placeholder-slate-400 outline-none transition focus:w-56 focus:border-blue-400 focus:bg-white focus:ring-2 focus:ring-blue-100"
          />
        </div>
      </div>
    </div>
  );
};

const HERO_TAGS = ["500+ Installations", "24/7 Support", "Multi-Regional Support"];

const SectionHero = ({ query, onQueryChange }) => (
  <div className="relative overflow-hidden bg-linear-to-b from-blue-50 to-white px-6 pb-14 pt-20 md:pb-16 md:pt-28 text-center">
    <div
      className="pointer-events-none absolute inset-0 opacity-[0.06]"
      style={{
        backgroundImage:
          "linear-gradient(#3b82f6 1px, transparent 1px), linear-gradient(90deg, #3b82f6 1px, transparent 1px)",
        backgroundSize: "60px 60px",
      }}
    />
    <div className="pointer-events-none absolute left-1/4 top-0 h-72 w-72 -translate-x-1/2 rounded-full bg-blue-300/25 blur-[100px]" />
    <div className="pointer-events-none absolute right-1/4 bottom-0 h-64 w-64 translate-x-1/2 rounded-full bg-cyan-300/15 blur-[80px]" />

    <div className="relative z-10 mx-auto max-w-4xl">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="mb-4 inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-blue-600"
      >
        What We Do
      </motion.div>

      <motion.h1
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7, delay: 0.1 }}
        className="text-3xl font-extrabold leading-snug text-slate-900 md:text-5xl"
        style={{ fontFamily: "'DM Sans', sans-serif" }}
      >
        Advanced
        <span className="block bg-linear-to-r from-blue-700 via-blue-600 to-cyan-600 bg-clip-text text-transparent">
          Security Solutions
        </span>
      </motion.h1>

      <motion.p
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="mx-auto mt-5 max-w-xl text-base text-slate-600 md:text-lg"
      >
        A complete range of security, fire safety, and electrical infrastructure
        systems - designed, installed, and supported to enterprise standards.
      </motion.p>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.35 }}
        className="mt-8 flex flex-wrap justify-center gap-3"
      >
        {HERO_TAGS.map((s) => (
          <span
            key={s}
            className="rounded-full border border-slate-200 bg-white px-4 py-1.5 text-xs font-medium text-slate-600"
          >
            {s}
          </span>
        ))}
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.45 }}
        className="relative mx-auto mt-10 max-w-xl"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="pointer-events-none absolute left-5 top-1/2 h-5 w-5 -translate-y-1/2 text-blue-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 10a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <input
          type="search"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          placeholder="Search products, e.g. dome camera, fire alarm..."
          aria-label="Search products"
          className="w-full rounded-2xl border-2 border-blue-100 bg-white py-4 pl-13 pr-12 text-base text-slate-800 placeholder-slate-400 shadow-lg shadow-blue-900/5 outline-none transition-all duration-200 focus:border-blue-400 focus:shadow-blue-200/40 focus:ring-4 focus:ring-blue-100"
        />
        {query && (
          <button
            type="button"
            onClick={() => onQueryChange("")}
            aria-label="Clear search"
            className="absolute right-4 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-700"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </motion.div>
    </div>
  </div>
);

const CategoryHeader = ({ title, color, index, count }) => (
  <motion.div
    initial={{ opacity: 0, x: -30 }}
    whileInView={{ opacity: 1, x: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5 }}
    className="relative mb-8 flex items-center gap-5 overflow-hidden rounded-2xl bg-white border border-slate-200 px-6 py-5 shadow-sm"
  >
    <div className={`absolute left-0 top-0 h-full w-1 ${color}`} />
    <span
      className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 select-none text-8xl font-extrabold opacity-[0.04] text-slate-900"
      style={{ fontFamily: "'DM Sans', sans-serif" }}
    >
      {String(index + 1).padStart(2, "0")}
    </span>
    <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${color} text-white text-sm font-semibold`}>
      {String(index + 1).padStart(2, "0")}
    </div>
    <div>
      <h2
        className="text-xl font-extrabold text-slate-900 md:text-2xl"
        style={{ fontFamily: "'DM Sans', sans-serif" }}
      >
        {title}
      </h2>
      <p className="text-xs font-medium text-slate-400">
        {count} product{count === 1 ? "" : "s"}
      </p>
    </div>
  </motion.div>
);

const LoadingSkeleton = () => (
  <div aria-hidden="true" className="space-y-16 pt-6">
    {[0, 1].map((sectionIndex) => (
      <div key={sectionIndex} className="space-y-6">
        <div className="h-20 rounded-2xl border border-slate-100 bg-slate-100/80" />
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2].map((cardIndex) => (
            <div key={cardIndex} className="overflow-hidden rounded-2xl border border-slate-100 bg-white">
              <div className="h-48 bg-slate-100/90" />
              <div className="space-y-2 p-5">
                <div className="h-5 w-2/3 rounded bg-slate-100/90" />
                <div className="h-3 w-1/3 rounded bg-blue-100" />
                <div className="h-3 w-full rounded bg-slate-100/80" />
              </div>
            </div>
          ))}
        </div>
      </div>
    ))}
  </div>
);

const ErrorState = ({ onRetry }) => (
  <div className="mx-auto mt-10 max-w-lg rounded-2xl border border-red-100 bg-red-50 px-6 py-10 text-center">
    <p className="text-sm font-semibold text-red-700">
      Couldn't load our product catalog right now.
    </p>
    <p className="mt-1 text-sm text-red-600">
      This usually means the site couldn't reach the server. Check your
      connection and try again.
    </p>
    <button
      type="button"
      onClick={onRetry}
      className="mt-4 rounded-xl bg-red-600 px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-red-700"
    >
      Retry
    </button>
  </div>
);

const EmptyState = () => (
  <div className="mx-auto mt-10 max-w-lg rounded-2xl border border-slate-200 bg-slate-50 px-6 py-10 text-center">
    <p className="text-sm font-medium text-slate-600">
      No product categories have been published yet — check back soon.
    </p>
  </div>
);

const NoResultsState = ({ query, onClear }) => (
  <div className="mx-auto mt-10 max-w-lg rounded-2xl border border-slate-200 bg-slate-50 px-6 py-10 text-center">
    <p className="text-sm font-medium text-slate-700">
      No products match "{query}".
    </p>
    <p className="mt-1 text-xs text-slate-500">
      Try a different keyword, or browse the full catalog.
    </p>
    <button
      type="button"
      onClick={onClear}
      className="mt-4 rounded-xl border border-slate-300 px-5 py-2 text-sm font-semibold text-slate-700 transition-colors hover:border-blue-400 hover:text-blue-600"
    >
      Clear Search
    </button>
  </div>
);

export default function ProductsSection() {
  const { categories, status, refetch } = useProductCategories();
  const [activeIndex, setActiveIndex] = useState(0);
  const [query, setQuery] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const observerRef = useRef(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // While a click (or ?category= link) triggers a smooth scroll, the target
  // section passes several other sections on the way there. Each one briefly
  // crosses the IntersectionObserver's thresholds mid-flight, which would
  // otherwise overwrite activeIndex with whatever's passing by instead of
  // the section actually being scrolled to. This ref tells the observer to
  // ignore intersection changes until that scroll has settled.
  const suppressObserverRef = useRef(false);
  const suppressTimeoutRef = useRef(null);

  // scroll-mt in CSS is a fixed number, but ViewNavBar's header height
  // changes (it shrinks after 20px of scroll), so a fixed offset that's
  // correct once the header has shrunk isn't tall enough on a fresh page
  // load, where the header is still at its taller, unshrunk size. Measuring
  // the actual header and category-nav heights at scroll time handles both.
  const getStickyOffset = () => {
    const header = document.querySelector("header");
    const categoryNav = document.getElementById("product-category-nav");
    const headerHeight = header?.getBoundingClientRect().height ?? 0;
    const navHeight = categoryNav?.getBoundingClientRect().height ?? 0;
    return headerHeight + navHeight + 16; // +16px breathing room
  };

  const scrollToId = (id, index) => {
    setActiveIndex(index);
    suppressObserverRef.current = true;
    if (suppressTimeoutRef.current) clearTimeout(suppressTimeoutRef.current);

    const el = document.getElementById(`section-${id}`);
    if (el) {
      // Set the scroll-margin dynamically so scrollIntoView stops just below
      // the (dynamically-sized) sticky header + category nav, instead of under
      // it. Using the browser's native scrollIntoView keeps the destination
      // accurate: the category sections no longer use content-visibility, so
      // their real positions are always known. (Previously they were rendered
      // lazily with an intrinsic-size approximation, which made both manual
      // measurement and scrollIntoView land on the wrong spot.)
      el.style.scrollMarginTop = `${getStickyOffset()}px`;
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }

    const resume = () => {
      suppressObserverRef.current = false;
      window.removeEventListener("scrollend", resume);
    };

    if ("onscrollend" in window) {
      window.addEventListener("scrollend", resume, { once: true });
    } else {
      // Fallback for browsers without the scrollend event: generous enough
      // to cover any reasonable smooth-scroll distance on this page.
      suppressTimeoutRef.current = setTimeout(resume, 900);
    }
  };

  const handleEnquire = (productTitle) => {
    setSelectedProduct(null);
    navigate("/contact", {
      state: {
        prefillMessage: `Hi, I would like to enquire about: ${productTitle}. Please provide more details on pricing, availability, and installation.`,
      },
    });
  };

  // Ranks how well a product matches the search query, higher is more
  // relevant. Exact/leading title matches outrank a word buried in the long
  // description, so the thing someone actually typed shows up first.
  const scoreProductMatch = (product, q) => {
    const title = (product.title ?? "").toLowerCase();
    const description = (product.description ?? "").toLowerCase();
    const detailed = (product.detailedDescription ?? "").toLowerCase();

    if (title === q) return 100;
    if (title.startsWith(q)) return 90;
    if (title.includes(q)) return 70;
    if (description.startsWith(q)) return 55;
    if (description.includes(q)) return 40;
    if (detailed.includes(q)) return 20;
    return 0;
  };

  // Categories filtered and ranked by the search box. Empty query = show
  // everything in its normal order. With a query, products are sorted
  // most-relevant-first within each category, and categories themselves are
  // reordered so the one containing the best overall match appears first.
  const filteredCategories = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return categories;

    return categories
      .map((category) => {
        const scored = (category.products ?? [])
          .map((product) => ({ product, score: scoreProductMatch(product, q) }))
          .filter((entry) => entry.score > 0)
          .sort((a, b) => b.score - a.score);

        return {
          ...category,
          products: scored.map((entry) => entry.product),
          bestScore: scored[0]?.score ?? 0,
        };
      })
      .filter((category) => category.products.length > 0)
      .sort((a, b) => b.bestScore - a.bestScore);
  }, [categories, query]);

  // Keep activeIndex valid when search shrinks the visible category list.
  useEffect(() => {
    if (activeIndex >= filteredCategories.length) {
      setActiveIndex(0);
    }
  }, [filteredCategories, activeIndex]);

  // Jump to a category requested via ?category=<id>, e.g. from the homepage
  // solutions grid. Runs once categories are actually in the DOM, so there's
  // no race with the fetch, no polling or guessed delays needed.
  //
  // React Router doesn't reset scroll position between route changes, so
  // arriving here from a scrolled-down homepage means the browser is still
  // sitting at that old scrollY. Animating from an arbitrary inherited
  // position (rather than a known one) is what made this path behave
  // differently from clicking a tab while already on this page. Snapping to
  // the top first, instantly, before the deliberate smooth scroll fixes that.
  useEffect(() => {
    if (status !== "ready") return;

    const targetId = searchParams.get("category");
    if (!targetId) return;

    const idx = categories.findIndex((c) => c._id === targetId);
    if (idx === -1) return;

    window.scrollTo({ top: 0, behavior: "auto" });
    requestAnimationFrame(() => scrollToId(targetId, idx));
    // scrollToId is stable across renders (only reads refs/DOM), safe to omit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, categories, searchParams]);

  // Covers arriving at /products with no ?category= at all, e.g. via the
  // "Products" nav link or "View All Solutions", same inherited-scroll issue
  // as above, just landing mid-page instead of at the hero.
  useEffect(() => {
    if (searchParams.get("category")) return; // the effect above handles this case
    window.scrollTo({ top: 0, behavior: "auto" });
    // Intentionally runs only once on mount for this route.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    observerRef.current?.disconnect();

    if (status !== "ready") return undefined;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (suppressObserverRef.current) return;

        const visibleEntry = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];

        if (!visibleEntry) return;

        const nextIndex = Number(visibleEntry.target.dataset.sectionIndex);
        setActiveIndex((currentIndex) =>
          currentIndex === nextIndex ? currentIndex : nextIndex,
        );
      },
      { threshold: [0.3, 0.6, 0.9] },
    );

    filteredCategories.forEach((category) => {
      const el = document.getElementById(`section-${category._id}`);
      if (!el) return;
      observerRef.current.observe(el);
    });

    return () => observerRef.current?.disconnect();
  }, [status, filteredCategories]);

  const scrollToSection = (index) => {
    scrollToId(filteredCategories[index]?._id, index);
  };

  // Clear any pending suppress-timeout if the component unmounts mid-scroll.
  useEffect(() => {
    return () => {
      if (suppressTimeoutRef.current) clearTimeout(suppressTimeoutRef.current);
    };
  }, []);

  // Modal ergonomics: Escape closes it, and the page behind it doesn't
  // scroll while it's open.
  useEffect(() => {
    if (!selectedProduct) return undefined;

    const onKeyDown = (e) => {
      if (e.key === "Escape") setSelectedProduct(null);
    };

    document.addEventListener("keydown", onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [selectedProduct]);

  return (
    <section id="WhatWeDo" className="bg-white">
      <SectionHero query={query} onQueryChange={setQuery} />

      {status === "ready" && filteredCategories.length > 0 && (
        <CategoryNav
          categories={filteredCategories}
          activeIndex={activeIndex}
          onSelect={scrollToSection}
          query={query}
          onQueryChange={setQuery}
        />
      )}

      <div className="mx-auto max-w-6xl px-4 pb-24 md:px-6 pt-6">
        {status === "loading" && <LoadingSkeleton />}
        {status === "error" && <ErrorState onRetry={refetch} />}
        {status === "empty" && <EmptyState />}
        {status === "ready" && query && filteredCategories.length === 0 && (
          <NoResultsState query={query} onClear={() => setQuery("")} />
        )}

        {status === "ready" &&
          filteredCategories.map((category, catIndex) => (
            <div
              key={category._id}
              id={`section-${category._id}`}
              data-section-index={catIndex}
              className="mb-16 scroll-mt-28"
            >
              <CategoryHeader
                title={category.title}
                color={category.color}
                index={catIndex}
                count={category.products.length}
              />

              <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {category.products.map((product, index) => (
                  <ProductCard
                    key={product._id}
                    product={product}
                    index={index}
                    query={query}
                    onViewDetails={setSelectedProduct}
                    onEnquire={handleEnquire}
                  />
                ))}
              </div>
            </div>
          ))}
      </div>

      <AnimatePresence>
        {selectedProduct && (
          <motion.div
            key="product-modal-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-60 flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm"
            onClick={() => setSelectedProduct(null)}
          >
            <motion.div
              key="product-modal"
              initial={{ opacity: 0, y: 20, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.98 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              onClick={(e) => e.stopPropagation()}
            >
              <LearnMore
                product={selectedProduct}
                onClose={() => setSelectedProduct(null)}
                onEnquire={handleEnquire}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}